/** Small DOM hooks shared by the FTUE renderer (PEL-516). */

import { useEffect, useState } from 'react'

export interface AnchorRect {
  top: number
  left: number
  width: number
  height: number
}

/**
 * Tracks an element's viewport rect, following it every animation frame.
 *
 * Used by the Spotlight cutout and the off-page badge. Per-frame polling (rather
 * than ResizeObserver/scroll listeners) is required because the anchor can move
 * for reasons those listeners never fire on: the app's page-entrance keyframe
 * animation (`animate-page-in`) pans/fades content in via a CSS *transform*,
 * which changes the element's visual rect without touching its layout box or
 * scrolling. A one-shot measure would strand the highlight at the element's
 * first-frame position. State only updates when the rect actually changes, so
 * steady-state cost is one cheap measure per frame and no re-render.
 */
export function useAnchorRect(element: HTMLElement | null): AnchorRect | null {
  const [rect, setRect] = useState<AnchorRect | null>(null)

  useEffect(() => {
    if (!element) {
      setRect(null)
      return
    }
    let frame = 0
    let prev = ''
    const measure = () => {
      const r = element.getBoundingClientRect()
      const next = `${r.top},${r.left},${r.width},${r.height}`
      if (next !== prev) {
        prev = next
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
      }
    }
    measure() // synchronous first paint — no one-frame gap before the cutout shows
    const tick = () => {
      measure()
      frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [element])

  return rect
}

/**
 * Nearest scrollable ancestor of `el` (including itself) — the element whose own
 * overflow contains the scrolling content. Shared by the opt-in `lockScroll`
 * behaviour (freeze the surface the spotlight sits on) and the spotlight cutout
 * clip (keep the highlight within that scroll viewport). Returns null when none.
 *
 * `hidden` counts (alongside `auto`/`scroll`): while `lockScroll` is active it sets
 * the scroll container's overflow to `hidden`, but it is still the same scroll
 * viewport (`scrollHeight > clientHeight`) and the clip must keep resolving to it.
 * The `scrollHeight > clientHeight` guard keeps this from matching incidental
 * `overflow: hidden` boxes that aren't actually clipping overflowing content.
 */
export function findScrollableAncestor(el: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = el
  while (node) {
    const overflowY = getComputedStyle(node).overflowY
    const scrolls = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden'
    if (scrolls && node.scrollHeight > node.clientHeight) return node
    node = node.parentElement
  }
  return null
}

/**
 * A bottom-pinned `position: sticky` descendant of `container`, if any — e.g. the
 * job-detail overlay's action band (Generate / Learn), which floats over the
 * scrolling content on its own layer. Used to clip the spotlight cutout ABOVE it
 * so the highlight stops at that fixed layer's boundary instead of running under
 * it. Returns the first match in document order, or null when there is none.
 */
export function findBottomStickyDescendant(container: HTMLElement | null): HTMLElement | null {
  if (!container) return null
  for (const node of container.querySelectorAll<HTMLElement>('*')) {
    const cs = getComputedStyle(node)
    if (cs.position === 'sticky' && Number.parseFloat(cs.bottom) === 0) return node
  }
  return null
}

/** True when the user has requested reduced motion. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}
